import { PlusIcon, TrashIcon } from '@phosphor-icons/react'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SelectFormField } from '@/components/form'
import { Button } from '@/components/ui/button'
import { Collapse } from '@/components/ui/collapse'
import { Flex } from '@/components/ui/flex'
import { Grid } from '@/components/ui/grid'
import { Icon } from '@/components/ui/icon'
import { SelectOption } from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'

import { ServiceFeeField } from './service-fee-field'
import {
  formatServiceFee,
  getServiceTypeOptions,
  ServiceFeeType,
  TrainerFormData,
} from './trainer-form-modal.constants'
import * as S from './trainer-services-field.styled'

// Note: matches the Collapse 'medium' timeout - the row is only removed from the form once it has finished collapsing
const ROW_REMOVE_DELAY_MS = 300

interface ServiceRowProps {
  // Note: only rows added by the user animate in - rows that were there when the modal opened appear instantly
  animateIn: boolean
  children: ReactNode
  isRemoving: boolean
  onRemoved: VoidFunction
}

const ServiceRow = ({ animateIn, children, isRemoving, onRemoved }: ServiceRowProps) => {
  const [isEntered, setIsEntered] = useState(!animateIn)
  const onRemovedRef = useRef(onRemoved)

  useEffect(() => {
    onRemovedRef.current = onRemoved
  })

  useEffect(() => {
    if (isEntered) return
    // Note: the row has to paint collapsed once before expanding, otherwise there is nothing to transition from
    const frame = requestAnimationFrame(() => setIsEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [isEntered])

  useEffect(() => {
    if (!isRemoving) return
    const timeout = setTimeout(() => onRemovedRef.current(), ROW_REMOVE_DELAY_MS)
    return () => clearTimeout(timeout)
  }, [isRemoving])

  return (
    <Collapse in={isEntered && !isRemoving}>
      <S.Row>{children}</S.Row>
    </Collapse>
  )
}

interface TrainerServicesFieldProps {
  // Note: blocks removing the last row - BE would silently ignore an empty list on update
  keepAtLeastOne?: boolean
  serviceOptions: SelectOption<string>[]
}

export const TrainerServicesField = ({
  keepAtLeastOne,
  serviceOptions,
}: TrainerServicesFieldProps) => {
  const { t } = useTranslation(['organisation'])
  const {
    control,
    formState: { errors },
  } = useFormContext<TrainerFormData>()
  const { append, fields, remove } = useFieldArray({ control, name: 'services' })
  const services = useWatch({ control, name: 'services' })
  const [initialRowIds] = useState(() => new Set(fields.map((field) => field.id)))
  const [removingRowIds, setRemovingRowIds] = useState<Set<string>>(new Set())

  const serviceTypeOptions = useMemo(() => getServiceTypeOptions(t), [t])

  const activeRowsCount = fields.length - removingRowIds.size

  const getRowOptions = (rowIndex: number) => {
    const takenByOtherRows = new Set(
      services.filter((_, index) => index !== rowIndex).map((service) => service.name),
    )
    return serviceOptions.filter((option) => !takenByOtherRows.has(option.value))
  }

  const handleRemoveRow = (rowId: string) => {
    setRemovingRowIds((current) => new Set(current).add(rowId))
  }

  const handleRowRemoved = (rowId: string) => {
    remove(fields.findIndex((field) => field.id === rowId))
    setRemovingRowIds((current) => {
      const next = new Set(current)
      next.delete(rowId)
      return next
    })
  }

  const servicesError = errors.services?.message ?? errors.services?.root?.message

  return (
    <Flex direction="column" gap={8}>
      <Typography color="black800" size="text-small" weight="semibold">
        {t('organisation:staff.form.services.label')}
      </Typography>

      {fields.length > 0 && (
        <S.Rows>
          {fields.map((field, index) => (
            <ServiceRow
              key={field.id}
              animateIn={!initialRowIds.has(field.id)}
              isRemoving={removingRowIds.has(field.id)}
              onRemoved={() => handleRowRemoved(field.id)}
            >
              <Grid align="start" columns="minmax(0, 1fr) 17rem 10rem auto" gap={8}>
                <SelectFormField<TrainerFormData, string>
                  searchable
                  name={`services.${index}.name`}
                  options={getRowOptions(index)}
                  placeholder={t('organisation:staff.form.services.placeholder')}
                  size="medium"
                  type="single"
                />
                <SelectFormField<TrainerFormData, ServiceFeeType>
                  name={`services.${index}.type`}
                  options={serviceTypeOptions}
                  size="medium"
                  type="single"
                />
                <ServiceFeeField index={index} />
                <Button
                  onlyBox
                  disabled={
                    removingRowIds.has(field.id) || (keepAtLeastOne && activeRowsCount === 1)
                  }
                  title={t('organisation:staff.form.services.remove')}
                  use="secondary"
                  onClick={() => handleRemoveRow(field.id)}
                >
                  <Icon icon={TrashIcon} color="red500" />
                </Button>
              </Grid>
            </ServiceRow>
          ))}
        </S.Rows>
      )}

      {servicesError && (
        <Typography color="red500" size="text-small">
          {servicesError}
        </Typography>
      )}

      <div>
        <Button
          size="small"
          use="secondary"
          onClick={() => append({ fee: formatServiceFee('0', 'rates'), name: '', type: 'rates' })}
        >
          <Icon icon={PlusIcon} />
          {t('organisation:staff.form.services.add')}
        </Button>
      </div>
    </Flex>
  )
}
